/**
 * Team Admin API calls
 */

import { UserRole } from "@speakai/shared";
import { apiClient } from "@/lib/api/client";
import type { FullPermission } from "@/config/permission-defaults";

/** Permission shape on TeamMember/TeamInvite — partial; matches the server response. */
export interface TeamMemberPermission {
  role?: UserRole;
  folder?: Record<string, boolean>;
  recorder?: Record<string, boolean>;
  media?: Record<string, boolean>;
  payment?: Record<string, boolean>;
  teamManagement?: Record<string, boolean>;
  developer?: Record<string, boolean>;
  profileSettings?: Record<string, boolean>;
  meetingAssistant?: Record<string, boolean>;
  labels?: Record<string, boolean>;
  comments?: Record<string, boolean>;
}

// -- Types -----------------------------------------------------------

export interface TeamMemberListParams {
  page?: number;
  pageSize?: number;
  filterName?: string;
  sortBy?: string;
  sortDir?: string;
}

export interface TeamMemberListResponse {
  userList: TeamMember[];
  page: number | string;
  totalCount: number;
}

export interface TeamMember {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  picture?: string;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
  permission: TeamMemberPermission;
}

export type InviteLinkSurface = "account_menu" | "home_bar" | "team_page" | "invite_settings" | "invite_dialog" | "onboarding";

export interface TeamInvite {
  inviteId: string;
  shareUrl: string;
  customMessage?: string;
  expiresInDays?: number;
  expiresAt?: Date | null;
  allowedDomains?: string[];
  allowedMethods?: {
    emailPassword: boolean;
    google: boolean;
    microsoft: boolean;
  };
  permission?: TeamMemberPermission;
}

/**
 * POST /admin/teaminvite — server Joi sendTeamInvite only accepts these
 * two keys. allowedMethods is persisted separately via PUT
 * /admin/teaminvite/:inviteId before the POST runs.
 */
export interface SendInviteData {
  emails: string[];
  permission: Partial<FullPermission>;
}

export interface UpdateInviteData {
  customMessage?: string;
  expiresInDays?: number;
  allowedDomains?: string[];
  permission?: Partial<FullPermission>;
  allowedMethods?: {
    emailPassword: boolean;
    google: boolean;
    microsoft: boolean;
  };
}

/** Populated user shape returned in `UserGroup.usersList` (filtered server-side to active users). */
export interface UserGroupMember {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  picture?: string;
}

export interface UserGroup {
  _id: string;
  description: string;
  /**
   * Raw user-ID references stored on the group document. Treat as opaque IDs —
   * the populated user objects live on `usersList`. Kept for round-tripping to
   * the update endpoint.
   */
  users: string[];
  /** Populated active users (server filters out null/inactive). Use this for rendering. */
  usersList: UserGroupMember[];
  createdAt: string;
}

export interface CreateGroupData {
  description: string;
  users: string[];
}

export interface UpdateGroupData {
  _id: string;
  description: string;
  users: string[];
}

// -- Team Members ----------------------------------------------------

export async function getTeamMembers(
  params: TeamMemberListParams = {}
) {
  const {
    page = 0,
    pageSize = 25,
    filterName = "",
    sortBy = "createdAt",
    sortDir = "desc",
  } = params;

  return apiClient.get<TeamMemberListResponse>(
    `/admin/users?page=${page}&pageSize=${pageSize}&filterName=${filterName}&sortBy=${sortBy}:${sortDir}`
  );
}

export async function fetchAllTeamMembers(filterName = "") {
  return getTeamMembers({
    page: 0,
    pageSize: 1000,
    filterName,
    sortBy: "firstName",
    sortDir: "asc",
  });
}

export async function createTeamMember(data: Record<string, unknown>) {
  return apiClient.post<{ status: string; message: string }>(
    "/admin/user",
    data
  );
}

export async function updateTeamMember(data: Record<string, unknown>) {
  return apiClient.put<{ status: string; message: string }>(
    "/admin/user",
    data
  );
}

export async function removeTeamMember(userId: string) {
  return apiClient.delete<{ status: string; message: string }>(
    `/admin/user/${userId}`
  );
}

// -- Team Invites ----------------------------------------------------

export async function getTeamInvites(createDefault?: boolean) {
  const query = createDefault ? "?createDefault=true" : "";
  return apiClient.get<{ invite: TeamInvite | null }>(
    `/admin/teaminvite${query}`
  );
}

export async function sendTeamInvites(data: SendInviteData) {
  return apiClient.post<{
    status: string;
    message: string;
    sentCount?: number;
    failedEmails?: string[];
  }>("/admin/teaminvite", data);
}

export async function updateTeamInvite(
  inviteId: string,
  data: UpdateInviteData
) {
  return apiClient.put<{
    status: string;
    message: string;
    invite?: TeamInvite;
  }>(`/admin/teaminvite/${inviteId}`, data);
}

export async function resetTeamInviteLink(inviteId: string) {
  return apiClient.post<{
    status: string;
    message: string;
    invite?: TeamInvite;
  }>(`/admin/teaminvite/${inviteId}/reset`, {});
}

// -- User Groups -----------------------------------------------------

export async function getGroups() {
  return apiClient.get<UserGroup[]>("/admin/usergroup");
}

export async function createGroup(data: CreateGroupData) {
  return apiClient.post<{ status: string; message: string }>(
    "/admin/usergroup",
    data
  );
}

export async function updateGroup(data: UpdateGroupData) {
  return apiClient.put<{ status: string; message: string }>(
    "/admin/usergroup",
    data
  );
}

export async function deleteGroup(groupId: string) {
  return apiClient.delete<{ status: string; message: string }>(
    `/admin/usergroup/${groupId}`
  );
}
